/**
 * Screen Flow Context
 * React Context integration with ScreenFlowManager and Session State Persistence
 */

import {
  createContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { ScreenFlowManager } from '../core/ScreenFlowManager';
import type {
  ScreenType,
  ScreenFlowState,
} from '../types/screens';
import {
  sessionStateManager,
  updateSessionState,
  getSessionState,
  type VerificationSessionState,
} from '../utils/SessionStateManager';
import { analyticsService } from '../services/AnalyticsService';
import type {
  ScreenFlowContextType,
  ScreenFlowProviderProps,
} from '../types/contexts';

const ScreenFlowContext = createContext<ScreenFlowContextType | null>(null);

export { ScreenFlowContext };

// Helper function to get display names for screens
function getScreenDisplayName(screen: ScreenType): string {
  const screenNames: Record<ScreenType, string> = {
    main: 'Landing Page',
    'emblem-step-up': 'Emblem Step Up',
    'camera-access': 'Camera Access',
    'camera-setup': 'Selfie Capture',
    'desktop-waiting': 'Waiting for Mobile',
    'id-request': 'ID Document Request',
    'id-capture': 'ID Document Capture',
    'id-review': 'ID Document Review',
    'id-processing': 'Processing',
    'selfie-retry': 'Selfie Retry',
    'auto-return': 'Auto Return',
    success: 'Verification Success',
    failure: 'Verification Failed',
    recovery: 'Session Recovery',
    loading: 'Loading',
    'return-to-desktop': 'Return to Desktop',
  };

  return screenNames[screen] || screen;
}

export function ScreenFlowProvider({
  children,
  initialScreen = 'main',
  onScreenChange,
  enableSessionPersistence = true,
}: ScreenFlowProviderProps) {
  const [manager] = useState(() => new ScreenFlowManager(initialScreen));
  const [state, setState] = useState<ScreenFlowState>(manager.getState());
  const [isEmbedded] = useState(() => window.self !== window.top);
  const [sessionState, setSessionState] =
    useState<VerificationSessionState | null>(null);
  // Navigation callbacks must run synchronously from the flow manager's
  // transition notification. Child mount effects may initialize transports,
  // so deferring lifecycle alignment to a provider effect creates a race.
  const transitionScreenRef = useRef<ScreenType>(initialScreen);

  // Listen for postMessage events from parent (if embedded)
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!isEmbedded) return;

      // Validate origin - this should be configurable
      // For now, accept all origins during development

      const { type, data } = event.data;

      switch (type) {
        case 'navigate':
          if (data?.screen) {
            manager.getActions().navigateToScreen(data.screen, data.screenData);
          }
          break;
        case 'goBack':
          manager.getActions().goBack();
          break;
        case 'reset':
          manager.getActions().resetFlow();
          break;
      }
    },
    [isEmbedded, manager]
  );

  useEffect(() => {
    // Initialize session state
    if (enableSessionPersistence) {
      sessionStateManager.setEnabled(true);

      const currentSessionState = getSessionState();
      setSessionState(currentSessionState);
    }

    // Subscribe to state changes
    const unsubscribe = manager.subscribe((newState) => {
      const previousScreen = transitionScreenRef.current;
      if (newState.currentScreen !== previousScreen) {
        transitionScreenRef.current = newState.currentScreen;
        onScreenChange?.(previousScreen, newState.currentScreen);
      }
      setState(newState);

      // Update session state when screen flow changes
      if (enableSessionPersistence) {
        updateSessionState({
          screenFlow: {
            currentScreen: newState.currentScreen,
            screenHistory: newState.screenHistory,
            screenData: newState.screenData,
            progress: newState.progress,
            completedSteps: [],
          },
        });
      }
    });

    // Re-read after subscribing: child effects (e.g., a route guard
    // redirect) run before this provider's effect on mount, so a navigation
    // can occur between the initial render and this subscription. Without
    // this sync that transition would be silently lost.
    setState(manager.getState());

    window.addEventListener('message', handleMessage);

    // Notify parent that we're ready (if embedded)
    if (isEmbedded) {
      window.parent.postMessage(
        {
          type: 'screen-flow-ready',
          data: { currentScreen: manager.getState().currentScreen },
        },
        '*'
      );
    }

    return () => {
      unsubscribe();
      window.removeEventListener('message', handleMessage);
    };
  }, [
    manager,
    isEmbedded,
    enableSessionPersistence,
    handleMessage,
    onScreenChange,
  ]);

  // Track committed screen changes for analytics. Lifecycle alignment is
  // initiated synchronously by the manager subscription above.
  const previousScreenRef = useRef<ScreenType | null>(null);

  useEffect(() => {
    const currentScreen = state.currentScreen;
    const previousScreen = previousScreenRef.current;

    // Only track if screen actually changed
    if (currentScreen !== previousScreen) {
      // Update ref first
      previousScreenRef.current = currentScreen;

      // Track page view in analytics
      analyticsService.trackPageView(
        currentScreen,
        getScreenDisplayName(currentScreen)
      );
    }
  }, [state.currentScreen]);

  // Function to restore screen flow from session state
  const restoreFromSession = useCallback(
    (sessionStateToRestore: VerificationSessionState) => {
      const { screenFlow } = sessionStateToRestore;

      // Restore screen flow state
      const currentScreenType = screenFlow.currentScreen as ScreenType;
      const screenData = screenFlow.screenData[currentScreenType];
      manager.getActions().navigateToScreen(currentScreenType, screenData);

      // Update session state reference
      setSessionState(sessionStateToRestore);
    },
    [manager]
  );

  // Memoize actions to prevent unnecessary re-renders
  const actions = useMemo(() => manager.getActions(), [manager]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue: ScreenFlowContextType = useMemo(
    () => ({
      state,
      actions,
      manager,
      isEmbedded,
      sessionState,
      restoreFromSession,
    }),
    [state, actions, manager, isEmbedded, sessionState, restoreFromSession]
  );

  return (
    <ScreenFlowContext.Provider value={contextValue}>
      {children}
    </ScreenFlowContext.Provider>
  );
}
